import { Icon } from "@studio3t/generic-components";
import { useCallback, useMemo } from "react";
import { playgroundApi, scriptsApi } from "@/api/client";
import {
  fetchSchemaDefinition,
  type StagedResource,
  type StagedSchema,
  schemaEditorId,
  schemaInstanceId,
  unattachedSchemaEditorId,
  untitledScriptIds,
  usePipelineStaging,
} from "./pipelineStaging";
import {
  resolveScriptContent,
  type ScriptSources,
  stagedScriptContents,
} from "./scriptContent";
import {
  clearRunId,
  pipelineRunRequest,
  type ScriptRunOutput,
  scriptRunRequest,
  scriptRunResult,
} from "./scriptRun";
import { isPendingOn } from "./stagingModel";

/** The runningId used while the whole pipeline runs (vs a row id). */
export const PIPELINE_RUN_ID = "pipeline";

type Drafts = Record<string, string>;

// The block schema's CURRENT body: the slot instance's unsaved draft when
// one exists, the stored definition otherwise.
async function resolveSchemaBody(
  schemaDrafts: Drafts,
  blockRowId: string,
  schema: StagedSchema | undefined,
): Promise<string | null | undefined> {
  if (!schema) return undefined;
  const draft = schemaDrafts[schemaInstanceId(schema.id, blockRowId)];
  if (draft != null) return draft;
  return await fetchSchemaDefinition({
    id: schema.id,
    name: schema.name,
    icon: Icon.Schema,
  } as StagedResource);
}

// Reads a stored script's content for a row the Studio holds no text for.
const fetchScript = (serviceName: string, filename: string) =>
  scriptsApi.get(serviceName, filename).then((file) => file.content);

// A staged row's script: its draft, else the text the Studio saved for it,
// else its stored file — the same content the editor would show.
const scriptSources = (
  scriptDrafts: Drafts,
  savedScripts: Drafts,
): ScriptSources => ({
  drafts: scriptDrafts,
  saved: savedScripts,
  fetchScript,
});

// Run pipeline's schema: the LAST staged block's attached schema, or the
// bottom block's waiting one when nothing is staged.
function pipelineSchema(
  transforms: StagedResource[],
  schemas: StagedSchema[],
): { schema: StagedSchema | undefined; blockRowId: string } {
  const last = transforms[transforms.length - 1];
  if (!last) {
    return {
      schema: schemas.find((s) => isPendingOn(s, "transform")),
      blockRowId: unattachedSchemaEditorId("transform"),
    };
  }
  return {
    schema: schemas.find((s) =>
      s.attachments.some((a) => a.role === "transform" && a.id === last.id),
    ),
    blockRowId: schemaEditorId(last.id),
  };
}

// Request builders for the three run flavours — module-level so the hook
// below stays a thin binding layer.
interface RunInputs {
  scriptDrafts: Drafts;
  savedScripts: Drafts;
  schemaDrafts: Drafts;
  inputDocument: string;
}

async function rowRunRequest(
  inputs: RunInputs,
  item: StagedResource,
  schema: StagedSchema | undefined,
) {
  return scriptRunRequest({
    script: await resolveScriptContent(
      scriptSources(inputs.scriptDrafts, inputs.savedScripts),
      item.id,
    ),
    payload: inputs.inputDocument,
    schemaBody: await resolveSchemaBody(
      inputs.schemaDrafts,
      schemaEditorId(item.id),
      schema,
    ),
  });
}

async function chainRunRequest(
  inputs: RunInputs,
  transforms: StagedResource[],
  schemas: StagedSchema[],
) {
  const { schema, blockRowId } = pipelineSchema(transforms, schemas);
  return pipelineRunRequest({
    scripts: await pipelineScripts(
      transforms,
      inputs.scriptDrafts,
      inputs.savedScripts,
    ),
    payload: inputs.inputDocument,
    schemaBody: await resolveSchemaBody(
      inputs.schemaDrafts,
      blockRowId,
      schema,
    ),
  });
}

// Fires one request and publishes its outcome (result or user-facing
// error) into the Output panel, tracking the in-flight run id.
async function executeRun(
  deps: {
    setScriptRunningId: React.Dispatch<React.SetStateAction<string | null>>;
    showScriptRunOutput: (output: ScriptRunOutput) => void;
  },
  runId: string,
  scriptName: string,
  request: () => Promise<Parameters<typeof playgroundApi.test>[0]>,
): Promise<void> {
  deps.setScriptRunningId(runId);
  try {
    const response = await playgroundApi.test(await request());
    deps.showScriptRunOutput(scriptRunResult(scriptName, response));
  } catch (error) {
    deps.showScriptRunOutput({
      scriptName,
      success: false,
      text: error instanceof Error ? error.message : "Failed to run script",
    });
  } finally {
    deps.setScriptRunningId(clearRunId(runId));
  }
}

// Run pipeline's script chain: every staged transform in order, or the
// untitled editor draft alone when nothing is staged.
export async function pipelineScripts(
  transforms: StagedResource[],
  scriptDrafts: Drafts,
  savedScripts: Drafts,
): Promise<string[]> {
  if (transforms.length > 0) {
    return stagedScriptContents(
      scriptSources(scriptDrafts, savedScripts),
      transforms.map((item) => item.id),
    );
  }
  // Nothing staged: the untitled drafts run chained in creation order.
  return untitledScriptIds(scriptDrafts)
    .map((id) => scriptDrafts[id] ?? "")
    .filter((draft) => draft.trim() !== "");
}

// Runs staged scripts through /studio's playground endpoint
// (POST /transform/run) and publishes the result into the Output panel.
// The running id lives in the staging context; each Run button disables
// only for ITS OWN in-flight run (a row run must not make the toolbar's
// Run pipeline react), and the latest finished run wins the Output.
export function useScriptRun() {
  const {
    transforms,
    schemas,
    scriptDrafts,
    savedScripts,
    schemaDrafts,
    inputDocument,
    showScriptRunOutput,
    scriptRunningId,
    setScriptRunningId,
  } = usePipelineStaging();

  const publishRun = useCallback(
    (
      runId: string,
      scriptName: string,
      request: () => Promise<Parameters<typeof playgroundApi.test>[0]>,
    ) =>
      executeRun(
        { setScriptRunningId, showScriptRunOutput },
        runId,
        scriptName,
        request,
      ),
    [setScriptRunningId, showScriptRunOutput],
  );

  const inputs = useMemo(
    () => ({ scriptDrafts, savedScripts, schemaDrafts, inputDocument }),
    [scriptDrafts, savedScripts, schemaDrafts, inputDocument],
  );

  const runRow = useCallback(
    (item: StagedResource, schema: StagedSchema | undefined) =>
      publishRun(item.id, item.name, () => rowRunRequest(inputs, item, schema)),
    [publishRun, inputs],
  );

  // Run pipeline: every staged transform chained in staging order (the
  // engine feeds each stage's output to the next); the LAST block's schema
  // rides along — the endpoint applies it to the final output only. With
  // nothing staged, the untitled editor draft runs alone.
  const runPipeline = useCallback(
    () =>
      publishRun(PIPELINE_RUN_ID, "Pipeline", () =>
        chainRunRequest(inputs, transforms, schemas),
      ),
    [publishRun, inputs, transforms, schemas],
  );

  return { runRow, runPipeline, runningId: scriptRunningId };
}
